import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Truck, FileText, Building2, ShoppingBag, BarChart3, Heart, ArrowRight, Briefcase, MessageSquareQuote, BookOpen, FlaskConical, Calculator, Globe2, Compass, Layers } from 'lucide-react';
const SiteSearch = lazy(() => import('@/components/ui/SiteSearch').then(m => ({ default: m.SiteSearch })));
import ortemLogo from '@/assets/ortem-logo.webp';
import { useTheme } from '@/lib/theme';
import SkyToggle from '@/components/ui/sky-toggle';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const navResources = [
  {
    name: 'Portfolio',
    href: '/portfolio/',
    tagline: 'Our Work',
    description: 'Browse 50+ delivered projects across mobile, web, AI, and enterprise software. Real apps. Real outcomes.',
    icon: Briefcase,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-500/10',
  },
  {
    name: 'Blog',
    href: '/blog/',
    tagline: 'Insights & Guides',
    description: 'Deep-dive articles on AI, mobile development, cloud, and digital strategy written by Ortem engineers.',
    icon: BookOpen,
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-500/10',
  },
  {
    name: 'Innovation Lab',
    href: '/innovation-lab/',
    tagline: 'R&D & Products',
    description: 'Where Ortem experiments, prototypes, and ships its own SaaS products — Fleet Sync Pro, Azimuth, Residenta & more.',
    icon: FlaskConical,
    iconColor: 'text-violet-500',
    iconBg: 'bg-violet-500/10',
  },
  {
    name: 'App Cost Calculator',
    href: '/tools/app-cost-calculator/',
    tagline: 'Free Tool',
    description: 'Get a ballpark budget for your app in under 3 minutes. Configure features, platforms, and complexity — no sign-up needed.',
    icon: Calculator,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-500/10',
  },
  {
    name: 'AI Cost Calculator',
    href: '/tools/ai-cost-calculator/',
    tagline: 'Free Tool',
    description: 'See what an AI workflow costs per task and per month. RAG, extraction, chatbots and support agents, priced from published API rates.',
    icon: Calculator,
    iconColor: 'text-sky-500',
    iconBg: 'bg-sky-500/10',
  },
  {
    name: 'OSDPI Calculator',
    href: '/osdpi/',
    tagline: 'Free Framework',
    description: 'Score a software project\'s delivery readiness across 10 weighted dimensions plus a security gate — no sign-up needed.',
    icon: BarChart3,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-500/10',
  },
  {
    name: 'Testimonials',
    href: '/testimonials/',
    tagline: 'Client Stories',
    description: 'Hear directly from founders, CTOs, and product leaders who have shipped with Ortem.',
    icon: MessageSquareQuote,
    iconColor: 'text-rose-500',
    iconBg: 'bg-rose-500/10',
  },
  {
    name: 'Our Methodology',
    href: '/our-methodology/',
    tagline: 'BridgeMethod™',
    description: 'The structured delivery framework that closes the gap between offshore cost and onshore quality — sprint by sprint.',
    icon: Compass,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-500/10',
  },
  {
    name: 'Our Delivery Model',
    href: '/about-our-delivery-model/',
    tagline: 'How We Work',
    description: 'US-managed, sprint-based delivery with full transparency and senior engineering talent. No black boxes, no surprises.',
    icon: Layers,
    iconColor: 'text-sky-500',
    iconBg: 'bg-sky-500/10',
  },
  {
    name: 'Industries We Serve',
    href: '/industries/',
    tagline: 'Vertical Expertise',
    description: 'Healthcare, FinTech, logistics, fleet, eCommerce, EdTech and more — domain-aware software development across 10 industry verticals.',
    icon: Globe2,
    iconColor: 'text-teal-500',
    iconBg: 'bg-teal-500/10',
  },
];

const services = [
  { name: 'Custom App Development', href: '/services/custom-app-development/', description: 'Tailored software solutions for your unique needs' },
  { name: 'Web Design & Development', href: '/services/web-development/', description: 'Modern, responsive websites that convert' },
  { name: 'Mobile App Development', href: '/services/mobile-app-development/', description: 'Native & cross-platform mobile experiences' },
  { name: 'AI, ML & DL Solutions', href: '/services/ai-ml-solutions/', description: 'Intelligent automation & data-driven insights' },
  { name: 'Cloud & DevOps', href: '/services/cloud-devops/', description: 'Scalable infrastructure & continuous delivery' },
  { name: 'Cybersecurity Solutions', href: '/services/cybersecurity/', description: 'Enterprise-grade security & compliance' },
  { name: 'eCommerce Solutions', href: '/services/ecommerce/', description: 'End-to-end digital commerce platforms' },
  { name: 'Enterprise Mobility Solutions', href: '/services/enterprise-mobility-solutions/', description: 'MDM, BYOD & secure mobile workforce apps' },
  { name: 'MVP Development', href: '/services/mvp-development/', description: 'From idea to market-ready product in 8-12 weeks' },
  { name: 'SaaS Development', href: '/services/saas-development/', description: 'Multi-tenant SaaS platforms with billing & onboarding' },
  { name: 'Application Modernization', href: '/services/application-modernization/', description: 'Modernize legacy systems without disrupting operations' },
  { name: 'Data Analytics & BI', href: '/services/data-analytics-bi/', description: 'Dashboards, KPI definition & self-serve reporting' },
  { name: 'Data Engineering', href: '/services/data-engineering/', description: 'Data pipelines, warehouses & real-time streaming' },
  { name: 'Staff Augmentation', href: '/services/staff-augmentation/', description: 'Pre-vetted senior developers placed in 5–7 days' },
  { name: 'AI Agent Development', href: '/services/ai-agent-development/', description: 'Autonomous AI agents, multi-agent systems & workflows' },
  { name: 'LLM Integration', href: '/services/llm-integration/', description: 'GPT-4, Claude & private LLM integration for your product' },
  { name: 'HIPAA-Compliant Development', href: '/services/hipaa-compliant-development/', description: 'Healthcare software built for compliance from day one' },
  { name: 'Next.js Development', href: '/services/nextjs-development/', description: 'Core Web Vitals-optimised, SEO-first web apps' },
  { name: 'Dedicated Development Team', href: '/services/dedicated-development-team/', description: 'A self-managed pod of senior engineers on retainer' },
  { name: 'Outsource Software Development', href: '/services/outsource-software-development/', description: 'US-managed offshore delivery, 40–60% below agency rates' },
  { name: 'Enterprise Software Development', href: '/services/enterprise-software-development/', description: 'Custom platforms with deep system integration' },
  { name: 'Telehealth App Development', href: '/services/telehealth-app-development/', description: 'HIPAA-compliant video visits, EHR sync & e-prescribing' },
  { name: 'Hire AI Developers', href: '/services/hire-ai-developers/', description: 'Production LLM engineers placed in 5–10 days' },
  { name: 'Answer Engine Optimization', href: '/services/answer-engine-optimization/', description: 'Get cited in ChatGPT, Perplexity & Google AI Overviews' },
];

const navProducts = [
  {
    name: 'Fleet Sync Pro',
    href: '/products/fleet-sync-pro/',
    tagline: 'Fleet Management SaaS',
    description: 'Real-time vehicle tracking, work orders, maintenance reminders & fuel cost analytics. Built for logistics fleets of 10–10,000+ vehicles.',
    logo: '/assets/products/fleet-sync-pro.webp',
    logoBg: '',
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-500/10',
  },
  {
    name: 'Azimuth',
    href: '/products/azimuth/',
    tagline: 'AI Project Estimation & Proposals',
    description: 'Turn raw client briefs into structured estimates, PRDs, and branded proposals in minutes — not days. Built for agencies.',
    logo: '/assets/products/azimuth.webp',
    logoBg: '',
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-500/10',
  },
  {
    name: 'Residenta',
    href: '/products/residenta/',
    tagline: 'Property Management SaaS',
    description: 'AI-powered lease management, maintenance classification, rent collection & resident portal. Scales from 10 to 10,000+ units.',
    logo: '/assets/products/residenta.webp',
    logoBg: 'bg-[#0d1b3e]',
    iconColor: 'text-violet-500',
    iconBg: 'bg-blue-900/10',
  },
  {
    name: 'Smart Retail Engine',
    href: '/products/smart-retail-engine/',
    tagline: 'Unified Commerce & Retail Intelligence',
    description: 'Computer vision shelf monitoring, AI demand forecasting & omnichannel inventory sync for modern retail brands.',
    logo: '/assets/products/smart-retail-engine.webp',
    logoBg: '',
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-500/10',
  },
  {
    name: 'Skill Gauge',
    href: '/products/skill-gauge/',
    tagline: 'AI Talent Assessment Platform',
    description: 'Adaptive skill assessments powered by IRT, AI code review & org-wide skills matrix. White-label ready for HR teams.',
    logo: '/assets/products/skill-gauge.webp',
    logoBg: 'bg-[#1a0533]',
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-500/10',
  },
  {
    name: 'GymApp',
    href: '/products/gymapp/',
    tagline: 'Fitness Management SaaS',
    description: 'A multi-tenant fitness and gym SaaS platform. Features class scheduling, QR check-in, billing, and 4-tier access control.',
    logo: '/assets/products/gymapp.webp',
    logoBg: 'bg-blue-500/10',
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-500/10',
  },
];

function ProductMegaMenu() {
  const [active, setActive] = useState(navProducts[0]);

  return (
    <div className="flex gap-0">
      {/* Left: clean category list — Azilen style */}
      <div className="w-[200px] shrink-0 border-r border-border/50 pr-2 mr-4">
        <p className="px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Our Products</p>
        <ul className="flex flex-col">
          {navProducts.map((p) => {
            const isActive = active.name === p.name;
            return (
              <li key={p.name}>
                <NavigationMenuLink asChild>
                  {p.external ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => setActive(p)}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg transition-all cursor-pointer no-underline outline-none select-none
                        ${isActive ? 'text-primary font-semibold bg-primary/5' : 'text-foreground/80 font-medium hover:text-primary hover:bg-muted/60'}`}
                    >
                      <span className={`w-6 h-6 shrink-0 rounded flex items-center justify-center overflow-hidden ${p.logoBg}`}>
                        <img src={p.logo} alt={p.name} className="w-full h-full object-contain" />
                      </span>
                      <span className="truncate">{p.name}</span>
                      {isActive && <ChevronDown className="w-3 h-3 -rotate-90 text-primary ml-auto shrink-0" />}
                    </a>
                  ) : (
                    <Link
                      to={p.href}
                      onMouseEnter={() => setActive(p)}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg transition-all cursor-pointer no-underline outline-none select-none
                        ${isActive ? 'text-primary font-semibold bg-primary/5' : 'text-foreground/80 font-medium hover:text-primary hover:bg-muted/60'}`}
                    >
                      <span className={`w-6 h-6 shrink-0 rounded flex items-center justify-center overflow-hidden ${p.logoBg}`}>
                        <img src={p.logo} alt={p.name} className="w-full h-full object-contain" />
                      </span>
                      <span className="truncate">{p.name}</span>
                      {isActive && <ChevronDown className="w-3 h-3 -rotate-90 text-primary ml-auto shrink-0" />}
                    </Link>
                  )}
                </NavigationMenuLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right: rich description card */}
      <div className="flex-1 flex flex-col min-h-[300px]">
        {/* Accent header strip with product logo */}
        <div className={`rounded-t-xl px-5 pt-5 pb-4 ${active.iconBg} border border-border/30 border-b-0`}>
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl shadow-sm p-1.5 ${active.logoBg || 'bg-background/80'}`}>
              <img src={active.logo} alt={active.name} className="w-full h-full object-contain" />
            </span>
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-widest ${active.iconColor} opacity-80`}>{active.tagline}</div>
              <h3 className="text-base font-bold text-foreground leading-tight">{active.name}</h3>
            </div>
          </div>
        </div>

        {/* Description body */}
        <div className="flex-1 rounded-b-xl border border-border/30 border-t-0 bg-muted/20 px-5 py-4 flex flex-col justify-between">
          <p className="text-sm text-muted-foreground leading-relaxed">{active.description}</p>
          <div className="mt-4 flex items-center gap-4">
            {active.external ? (
              <a
                href={active.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold ${active.iconColor} hover:underline`}
              >
                Visit {active.name} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            ) : (
              <Link
                to={active.href}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold ${active.iconColor} hover:underline`}
              >
                View Product <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            <span className="text-muted-foreground/40 text-xs">|</span>
            <Link to="/products/" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              All Products →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResourcesMegaMenu() {
  const [active, setActive] = useState(navResources[0]);
  const ActiveIcon = active.icon;

  return (
    <div className="flex gap-0">
      {/* Left: clean category list */}
      <div className="w-[200px] shrink-0 border-r border-border/50 pr-2 mr-4">
        <p className="px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Explore</p>
        <ul className="flex flex-col">
          {navResources.map((r) => {
            const isActive = active.name === r.name;
            return (
              <li key={r.name}>
                <NavigationMenuLink asChild>
                  <Link
                    to={r.href}
                    onMouseEnter={() => setActive(r)}
                    className={`flex items-center justify-between w-full px-3 py-2.5 text-sm rounded-lg transition-all cursor-pointer no-underline outline-none select-none
                      ${isActive ? 'text-primary font-semibold bg-primary/5' : 'text-foreground/80 font-medium hover:text-primary hover:bg-muted/60'}`}
                  >
                    <span>{r.name}</span>
                    {isActive && <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-primary" />}
                  </Link>
                </NavigationMenuLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right: description card */}
      <div className="flex-1 flex flex-col min-h-[300px]">
        <div className={`rounded-t-xl px-5 pt-5 pb-4 ${active.iconBg} border border-border/30 border-b-0`}>
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-background/60 shadow-sm">
              <ActiveIcon className={`w-5 h-5 ${active.iconColor}`} />
            </span>
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-widest ${active.iconColor} opacity-80`}>{active.tagline}</div>
              <h3 className="text-base font-bold text-foreground leading-tight">{active.name}</h3>
            </div>
          </div>
        </div>
        <div className="flex-1 rounded-b-xl border border-border/30 border-t-0 bg-muted/20 px-5 py-4 flex flex-col justify-between">
          <p className="text-sm text-muted-foreground leading-relaxed">{active.description}</p>
          <div className="mt-4 flex items-center gap-4">
            <Link
              to={active.href}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${active.iconColor} hover:underline`}
            >
              Explore {active.name} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
        ? 'bg-background/90 backdrop-blur-xl border-b border-primary/10 shadow-sm'
        : 'bg-background/20 backdrop-blur-[2px] border-b border-transparent'
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src={ortemLogo}
              alt="Ortem Technologies"
              className="h-16 w-auto object-contain"
              width="64"
              height="64"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-semibold text-foreground/80 hover:text-primary transition-colors hover:bg-muted/60 rounded-lg"
            >
              Home
            </Link>

            {/* Services dropdown — own NavigationMenu so viewport aligns below this trigger */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground/80 font-semibold hover:text-primary hover:bg-muted/60">
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[600px] p-4 bg-background/95 backdrop-blur-xl rounded-2xl border border-primary/10 shadow-lg">
                      <NavigationMenuLink asChild>
                        <Link
                          to="/services/"
                          className="flex items-center justify-between select-none rounded-xl p-3 mb-2 leading-none no-underline outline-none transition-all bg-gradient-to-r from-primary/10 to-accent/5 hover:from-primary/15 hover:to-accent/10 group border border-primary/10"
                        >
                          <div>
                            <div className="text-sm font-semibold text-primary">
                              View All Services
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Explore our complete range of digital solutions
                            </p>
                          </div>
                          <ChevronDown className="w-4 h-4 text-primary -rotate-90" />
                        </Link>
                      </NavigationMenuLink>
                      <ul className="grid gap-1 md:grid-cols-2 max-h-[420px] overflow-y-auto pr-1">
                        {services.map((service) => (
                          <li key={service.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={service.href}
                                className="block select-none rounded-xl p-3 leading-none no-underline outline-none transition-all hover:bg-muted focus:bg-muted group"
                              >
                                <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                  {service.name}
                                </div>
                                <p className="line-clamp-2 text-xs text-muted-foreground mt-1">
                                  {service.description}
                                </p>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to="/case-studies/"
              className="px-4 py-2 text-sm font-semibold text-foreground/80 hover:text-primary transition-colors hover:bg-muted/60 rounded-lg"
            >
              Case Studies
            </Link>

            {/* Products mega-menu */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground/80 font-semibold hover:text-primary hover:bg-muted/60">
                    Products
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[600px] p-4 bg-background/98 backdrop-blur-xl rounded-2xl border border-border/60 shadow-xl">
                      <ProductMegaMenu />
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to="/about/"
              className="px-4 py-2 text-sm font-semibold text-foreground/80 hover:text-primary transition-colors hover:bg-muted/60 rounded-lg"
            >
              About
            </Link>

            {/* Resources mega-menu */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground/80 font-semibold hover:text-primary hover:bg-muted/60">
                    Resources
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[600px] p-4 bg-background/98 backdrop-blur-xl rounded-2xl border border-border/60 shadow-xl">
                      <ResourcesMegaMenu />
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to="/contact/"
              className="px-4 py-2 text-sm font-semibold text-foreground/80 hover:text-primary transition-colors hover:bg-muted/60 rounded-lg"
            >
              Contact
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Sky toggle */}
            <div className="border-2 border-primary/30 bg-background/50 backdrop-blur-sm rounded-xl hover:border-primary/60 transition-all duration-300 flex items-center justify-center px-3 h-10">
              <SkyToggle
                checked={resolvedTheme === 'dark'}
                onChange={toggleTheme}
                size="13px"
              />
            </div>
            <Suspense fallback={<div className="w-8 h-8" />}>
              <SiteSearch />
            </Suspense>
            <Button
              variant="hero"
              size="default"
              className="px-5 text-sm font-semibold"
              onClick={() => window.dispatchEvent(new CustomEvent('open-estimate-popup'))}
            >
              Get a Free Estimate
            </Button>
          </div>

          {/* Mobile right controls */}
          <div className="lg:hidden flex items-center gap-2">
            <Suspense fallback={<div className="w-8 h-8" />}>
              <SiteSearch />
            </Suspense>
            <button
              className="p-3 text-foreground rounded-xl bg-background/70 backdrop-blur-sm border border-primary/10 min-w-[44px] min-h-[44px] flex items-center justify-center active:scale-95 transition-transform"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Optimized for Usability */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-20 h-[calc(100dvh-5rem)] bg-background/95 backdrop-blur-xl z-40 overflow-y-auto overscroll-contain pb-20 animate-fade-in border-t border-primary/10">
            <nav className="flex flex-col p-4 gap-2">
              <Link to="/" className="flex items-center justify-between px-4 py-4 text-foreground hover:bg-muted rounded-xl transition-colors font-medium text-lg border-b border-border/40">
                Home
              </Link>
              <Link to="/services/" className="flex items-center justify-between px-4 py-4 text-foreground hover:bg-muted rounded-xl transition-colors font-medium text-lg border-b border-border/40">
                All Services
              </Link>

              <div className="px-4 py-3 text-xs font-bold text-primary uppercase tracking-widest mt-2">
                Services
              </div>
              <div className="grid grid-cols-1 gap-1 pl-2">
                {services.slice(0, 5).map((service) => (
                  <Link
                    key={service.name}
                    to={service.href}
                    className="px-4 py-3 text-base text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    {service.name}
                  </Link>
                ))}
                <Link
                  to="/services/"
                  className="px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/5 rounded-lg transition-colors"
                >
                  View all 15 services →
                </Link>
              </div>

              <div className="h-px bg-border my-2 mx-4" />

              <Link to="/case-studies/" className="px-4 py-4 text-foreground hover:bg-muted rounded-xl transition-colors text-lg font-medium">
                Case Studies
              </Link>

              <Link to="/products/" className="flex items-center justify-between px-4 py-4 text-foreground hover:bg-muted rounded-xl transition-colors font-medium text-lg border-b border-border/40">
                All Products
              </Link>
              <div className="px-4 py-3 text-xs font-bold text-primary uppercase tracking-widest mt-1">
                Products
              </div>
              <div className="grid grid-cols-1 gap-1 pl-2">
                {navProducts.slice(0, 4).map((p) =>
                  p.external ? (
                    <a
                      key={p.name}
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 text-base text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors"
                    >
                      <span className={`w-5 h-5 shrink-0 rounded flex items-center justify-center overflow-hidden ${p.logoBg}`}>
                        <img src={p.logo} alt={p.name} className="w-full h-full object-contain" />
                      </span>
                      {p.name}
                    </a>
                  ) : (
                    <Link
                      key={p.name}
                      to={p.href}
                      className="flex items-center gap-3 px-4 py-3 text-base text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors"
                    >
                      <span className={`w-5 h-5 shrink-0 rounded flex items-center justify-center overflow-hidden ${p.logoBg}`}>
                        <img src={p.logo} alt={p.name} className="w-full h-full object-contain" />
                      </span>
                      {p.name}
                    </Link>
                  )
                )}
                <Link to="/products/" className="px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/5 rounded-lg transition-colors">
                  View all products →
                </Link>
              </div>

              <Link to="/about/" className="px-4 py-4 text-foreground hover:bg-muted rounded-xl transition-colors text-lg font-medium">
                About
              </Link>

              <div className="px-4 py-3 text-xs font-bold text-primary uppercase tracking-widest mt-2">
                Resources
              </div>
              <div className="grid grid-cols-1 gap-1 pl-2">
                {navResources.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center gap-3 px-4 py-3 text-base text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors"
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${item.iconColor}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
              <Link to="/contact/" className="px-4 py-4 text-foreground hover:bg-muted rounded-xl transition-colors text-lg font-medium">
                Contact
              </Link>

              <div className="pt-6 px-2 pb-8 flex flex-col gap-3">
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border">
                  <SkyToggle
                    checked={resolvedTheme === 'dark'}
                    onChange={toggleTheme}
                    size="22px"
                  />
                  <span className="text-foreground font-medium text-base" suppressHydrationWarning>
                    {resolvedTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                  </span>
                </div>
                <Button
                  variant="hero"
                  className="w-full h-12 text-base font-semibold shadow-md"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-estimate-popup'));
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Get a Free Estimate
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
